/**
 * THE DESK'S SITUATION COMPARTMENTS
 *
 * The brief shows five situations: water, security, weather, air, and what is
 * happening in the streets. This module turns the live feeds into those five
 * tiles — one strong line each, plus the supporting numbers.
 *
 * Two rules keep it honest:
 *
 *  · THE BOARD OWNS THE ALERTING. The water tile's severity is read back out
 *    of the action board's own items, so the desk and the board can never
 *    disagree about how bad the river is. The bands below are DISPLAY bands —
 *    they decide which compartment is drawn largest, nothing else. They are
 *    named and pinned by unit tests.
 *  · A DEAD FEED IS "NO DATA", NEVER "ALL CLEAR". A compartment whose feed is
 *    not reporting says so, and names the source, rather than showing a
 *    reassuring zero.
 */

import type {
  AirDangerProvince,
  ConflictIncident,
  DamStatus,
  FloodGauge,
  IncidentFeature,
  PattaniRiverForecast,
  PrecipNowcast,
  WeatherSnapshot,
  YalaFloodOps,
} from "@yala/shared";
import type { ActionItem } from "./actionBoard";
import { THRESHOLDS } from "./actionBoard";
import { worstLevel, type BriefLevel, type BriefSignal } from "./mayorBrief";

/**
 * Display bands — how loud a situation compartment is drawn. Not alert
 * thresholds: `lib/actionBoard.ts` owns those, and the water tile reads its
 * severity from the board instead of from here.
 */
export const DESK_BANDS = {
  /** Conflicts reported in the last 24 hours → the security tile is "watch". */
  SECURITY_WATCH_24H: 1,
  /** Conflicts in the last 7 days at or above this count → "warning". */
  SECURITY_WARNING_7D: 3,
  /** Open municipal reports at or above this count → the street tile is "watch". */
  INCIDENTS_WATCH: 5,
  /** Open municipal reports at or above this count → "warning". */
  INCIDENTS_WARNING: 15,
} as const;

/** Action-board item ids that describe water. The water tile reads these. */
const WATER_ITEM_IDS = [
  "evacuees-active",
  "dam-critical",
  "dam-warning",
  "dam-watch",
  "gauge-flood",
  "gauge-warning",
  "river-emergency",
  "river-surge",
  "rain-critical",
  "rain-warning",
  "villages-affected",
];

const SEVERITY_RANK: Record<string, number> = {
  critical: 0,
  warning: 1,
  watch: 2,
  blindspot: 3,
};

/** The worst severity among the board's water items; null when there is none. */
export function waterSeverity(actions: ActionItem[]): ActionItem["severity"] | null {
  const water = actions.filter((a) => WATER_ITEM_IDS.includes(a.id));
  if (water.length === 0) return null;
  let worst: ActionItem["severity"] | null = null;
  for (const a of water) {
    if (worst === null || SEVERITY_RANK[a.severity] < SEVERITY_RANK[worst]) worst = a.severity;
  }
  return worst;
}

function pct(n: number | null | undefined): string {
  return n == null || Number.isNaN(n) ? "—" : `${Math.round(n)}%`;
}

function num(n: number | null | undefined, digits = 0, unit = ""): string {
  return n == null || Number.isNaN(n) ? "—" : `${n.toFixed(digits)}${unit}`;
}

function count(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString("en-US");
}

/** Is this timestamp inside the last `hours`? Future timestamps count as inside. */
export function withinHours(iso: string | null | undefined, hours: number, now = new Date()): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  const age = now.getTime() - t;
  return age <= hours * 3_600_000;
}

export interface DeskSignalsInput {
  dam: DamStatus | null;
  gauges: FloodGauge[];
  riverForecast: PattaniRiverForecast | null;
  precip: PrecipNowcast | null;
  floodOps: YalaFloodOps | null;
  airDanger: AirDangerProvince[];
  /** The ground station the dashboard's own AQI badge reads — same source, so
   *  the desk can never contradict the number on screen. */
  aqi: { station: string; pm25: number | null } | null;
  weather: WeatherSnapshot | null;
  conflicts: ConflictIncident[];
  /** Open municipal reports + iTIC events, already resolved by the caller. */
  incidents: IncidentFeature[];
  /** Ranked decisions from the action board. */
  actions: ActionItem[];
  /** Which feeds are reporting. A feed that is down yields "no data". */
  ok: {
    flood: boolean;
    security: boolean;
    weather: boolean;
    air: boolean;
    incidents: boolean;
  };
  /** Age of each feed's reading, in minutes. */
  ages: {
    flood: number | null;
    security: number | null;
    weather: number | null;
    air: number | null;
    incidents: number | null;
  };
  now?: Date;
}

const NO_DATA_VALUE = {
  en: "No current reading",
  th: "ไม่มีข้อมูลล่าสุด",
};
const NO_DATA_FACTS = {
  en: "This signal is not being monitored right now",
  th: "สัญญาณนี้ไม่ได้ถูกติดตามอยู่ในขณะนี้",
};

/**
 * The water severity, read from the data.
 *
 * The board already ranks these conditions, and the desk shows that ranking.
 * But a tile that trusts only the board would paint a gauge that IS flooding
 * as "good" the moment the board's item list and the tile's data disagree.
 * So the tile reads both and keeps the worse — and every threshold it uses
 * comes from the board's own THRESHOLDS, so there is exactly one set of
 * decision boundaries in the product.
 */
export function floodLevelFromData(i: Pick<
  DeskSignalsInput,
  "dam" | "gauges" | "riverForecast" | "precip" | "floodOps"
>): BriefLevel {
  const gauges = i.gauges;
  if (gauges.some((g) => g.status === "flood")) return "critical";
  if (i.riverForecast?.band === "emergency") return "critical";
  if (i.precip?.total2hMm != null && i.precip.total2hMm >= THRESHOLDS.RAIN_CRITICAL_2H_MM) return "critical";
  if (i.dam?.storagePct != null && i.dam.storagePct >= THRESHOLDS.DAM_CRITICAL_PCT) return "critical";
  if (i.floodOps && i.floodOps.evacueesTotal > 0) return "critical";
  if (gauges.some((g) => g.status === "warning")) return "warning";
  if (i.precip?.total2hMm != null && i.precip.total2hMm >= THRESHOLDS.RAIN_WARNING_2H_MM) return "warning";
  if (i.dam?.storagePct != null && i.dam.storagePct >= THRESHOLDS.DAM_WARNING_PCT) return "warning";
  if (i.dam?.storagePct != null && i.dam.storagePct >= THRESHOLDS.DAM_WATCH_PCT) return "watch";
  if (i.precip?.total2hMm != null && i.precip.total2hMm > 0) return "watch";
  return "good";
}

function floodTile(i: DeskSignalsInput): BriefSignal {
  if (!i.ok.flood) {
    return {
      key: "flood",
      level: "unknown",
      title: { en: "WATER", th: "น้ำ" },
      value: NO_DATA_VALUE,
      facts: NO_DATA_FACTS,
      source: "flooddash",
      ageMinutes: i.ages.flood,
    };
  }

  const dam = i.dam;
  const gauges = i.gauges;
  const flooding = gauges.filter((g) => g.status === "flood");
  const warning = gauges.filter((g) => g.status === "warning");
  const river = i.riverForecast;
  const rain = i.precip;

  const severity = waterSeverity(i.actions);
  // The board's ranking and the data's ranking must agree; when they do not,
  // the worse one wins. A gauge that IS flooding is never painted "good".
  // A blind spot is dropped from the comparison: it says "this signal is not
  // reporting", not "the water is fine", and it must not mask a reading we
  // actually hold.
  const boardLevel: BriefLevel =
    severity === null || severity === "blindspot"
      ? "good"
      : severity === "critical"
        ? "critical"
        : severity === "warning"
          ? "warning"
          : "watch";
  let level: BriefLevel = worstLevel([boardLevel, floodLevelFromData(i)]);

  let value: { en: string; th: string };
  let facts: { en: string; th: string } | undefined;

  if (flooding.length > 0) {
    const worst = [...flooding].sort((a, b) => (b.levelM ?? 0) - (a.levelM ?? 0))[0];
    value = {
      en: `${flooding.length} gauge${flooding.length > 1 ? "s" : ""} at flood level — ${worst.name} ${num(worst.levelM, 2, " m")}`,
      th: `สถานีวัดน้ำ ${flooding.length} แห่งอยู่ในระดับน้ำท่วม — ${worst.name} ${num(worst.levelM, 2, " ม.")}`,
    };
    facts = {
      en: `Dam ${pct(dam?.storagePct)} · outflow ${num(dam?.outflowCms, 0, " m³/s")}${warning.length ? ` · ${warning.length} more above warning` : ""}`,
      th: `เขื่อน ${pct(dam?.storagePct)} · ระบาย ${num(dam?.outflowCms, 0, " ม³/วิ")}${warning.length ? ` · เตือนภัยอีก ${warning.length} แห่ง` : ""}`,
    };
  } else if (river?.band === "emergency") {
    level = "critical";
    value = {
      en: `Pattani River forecast at emergency band — ${num(river.dischargeCms, 1, " m³/s")}, peak ${num(river.forecastPeakCms, 1, " m³/s")}`,
      th: `พยากรณ์แม่น้ำปัตตานีอยู่ในระดับฉุกเฉิน — ${num(river.dischargeCms, 1, " ม³/วิ")} สูงสุด ${num(river.forecastPeakCms, 1, " ม³/วิ")}`,
    };
  } else if (dam?.storagePct != null && dam.storagePct >= 70) {
    value = {
      en: `Bang Lang Dam at ${pct(dam.storagePct)} of capacity — outflow ${num(dam.outflowCms, 0, " m³/s")}`,
      th: `เขื่อนบางลางอยู่ที่ ${pct(dam.storagePct)} ของความจุ — ระบาย ${num(dam.outflowCms, 0, " ม³/วิ")}`,
    };
    facts = warning.length
      ? {
          en: `${warning.length} gauge${warning.length > 1 ? "s" : ""} above warning level`,
          th: `สถานีวัดน้ำ ${warning.length} แห่งเกินระดับเตือนภัย`,
        }
      : undefined;
  } else if (warning.length > 0) {
    value = {
      en: `${warning.length} gauge${warning.length > 1 ? "s" : ""} above warning level`,
      th: `สถานีวัดน้ำ ${warning.length} แห่งเกินระดับเตือนภัย`,
    };
  } else if (rain?.total2hMm != null && rain.total2hMm > 0) {
    level = rain.total2hMm >= 15 ? "watch" : "good";
    value = {
      en: `${num(rain.total2hMm, 1, " mm")} of rain forecast in the next 2 hours`,
      th: `พยากรณ์ฝน ${num(rain.total2hMm, 1, " มม.")} ใน 2 ชั่วโมงข้างหน้า`,
    };
  } else {
    value = {
      en: `Bang Lang Dam ${pct(dam?.storagePct)} · ${gauges.length} gauges normal`,
      th: `เขื่อนบางลาง ${pct(dam?.storagePct)} · สถานีวัดน้ำ ${gauges.length} แห่งปกติ`,
    };
  }

  const ops = i.floodOps;
  if (ops && ops.evacueesTotal > 0) {
    facts = {
      en: `${count(ops.evacueesTotal)} evacuees in ${ops.shelterCount} shelters · ${count(ops.affectedVillages)} villages affected`,
      th: `ผู้อพยพ ${count(ops.evacueesTotal)} คน ใน ${ops.shelterCount} ศูนย์ · หมู่บ้านได้รับผลกระทบ ${count(ops.affectedVillages)} แห่ง`,
    };
  }

  return {
    key: "flood",
    level,
    title: { en: "WATER", th: "น้ำ" },
    value,
    facts,
    source: "flooddash",
    ageMinutes: i.ages.flood,
  };
}

function securityTile(i: DeskSignalsInput): BriefSignal {
  if (!i.ok.security) {
    return {
      key: "security",
      level: "unknown",
      title: { en: "SECURITY", th: "ความมั่นคง" },
      value: NO_DATA_VALUE,
      facts: NO_DATA_FACTS,
      source: "acled",
      ageMinutes: i.ages.security,
    };
  }

  const now = i.now ?? new Date();
  const recent = i.conflicts.filter((c) => withinHours(c.date, 24, now));
  const week = i.conflicts.filter((c) => withinHours(c.date, 24 * 7, now));
  const deaths = week.reduce((sum, c) => sum + (c.fatalities || 0), 0);
  const yala = week.filter((c) => c.province === "Yala").length;

  let level: BriefLevel = "good";
  if (week.length >= DESK_BANDS.SECURITY_WARNING_7D || deaths > 0) level = "warning";
  else if (recent.length >= DESK_BANDS.SECURITY_WATCH_24H) level = "watch";

  const value =
    week.length === 0
      ? { en: "No conflict incidents reported in the last 7 days", th: "ไม่มีเหตุความไม่สงบใน 7 วันหลังสุด" }
      : {
          en: `${week.length} incident${week.length > 1 ? "s" : ""} in 7 days — ${recent.length} in the last 24h${deaths ? ` · ${deaths} death${deaths > 1 ? "s" : ""}` : ""}`,
          th: `${week.length} เหตุใน 7 วัน — ${recent.length} เหตุใน 24 ชม.${deaths ? ` · เสียชีวิต ${deaths} ราย` : ""}`,
        };

  const facts = yala > 0
    ? { en: `${yala} in Yala province · district aggregate, not individual points`, th: `${yala} เหตุในจังหวัดยะลา · รวมระดับอำเภอ ไม่ใช่จุดเหตุรายบุคคล` }
    : { en: "District aggregate — not individual incident points", th: "รวมระดับอำเภอ — ไม่ใช่จุดเหตุรายบุคคล" };

  return {
    key: "security",
    level,
    title: { en: "SECURITY", th: "ความมั่นคง" },
    value,
    facts,
    source: "acled",
    ageMinutes: i.ages.security,
  };
}

function weatherTile(i: DeskSignalsInput): BriefSignal {
  if (!i.ok.weather) {
    return {
      key: "weather",
      level: "unknown",
      title: { en: "WEATHER", th: "สภาพอากาศ" },
      value: NO_DATA_VALUE,
      facts: NO_DATA_FACTS,
      source: "open-meteo",
      ageMinutes: i.ages.weather,
    };
  }
  const w = i.weather;
  const rain = i.precip;
  const parts = [
    w?.tempC != null ? `${Math.round(w.tempC)}°C` : null,
    w?.humidity != null ? `${Math.round(w.humidity)}% RH` : null,
    rain?.total2hMm != null ? `${num(rain.total2hMm, 1, " mm")} / 2h` : null,
  ].filter(Boolean) as string[];

  // A feed that answered with nothing is still "no reading", not a reading of
  // zero. The feed being up says nothing about the weather.
  if (parts.length === 0 && !w?.condition) {
    return {
      key: "weather",
      level: "unknown",
      title: { en: "WEATHER", th: "สภาพอากาศ" },
      value: NO_DATA_VALUE,
      facts: NO_DATA_FACTS,
      source: "open-meteo",
      ageMinutes: i.ages.weather,
    };
  }

  const level: BriefLevel =
    rain?.total2hMm != null && rain.total2hMm >= 30 ? "warning" : rain?.total2hMm != null && rain.total2hMm >= 15 ? "watch" : "good";

  return {
    key: "weather",
    level,
    title: { en: "WEATHER", th: "สภาพอากาศ" },
    value: {
      en: parts.length ? parts.join(" · ") : w?.condition || "No reading",
      th: parts.length ? parts.join(" · ") : w?.condition || "ไม่มีข้อมูล",
    },
    facts: w?.condition ? { en: w.condition, th: w.condition } : undefined,
    source: "open-meteo",
    ageMinutes: i.ages.weather,
  };
}

function airTile(i: DeskSignalsInput): BriefSignal {
  if (!i.ok.air) {
    return {
      key: "air",
      level: "unknown",
      title: { en: "AIR", th: "คุณภาพอากาศ" },
      value: NO_DATA_VALUE,
      facts: NO_DATA_FACTS,
      source: "aqicn",
      ageMinutes: i.ages.air,
    };
  }
  const aqi = i.aqi;
  const danger = i.airDanger.find((p) => p.provinceNameEn === "Yala") ?? i.airDanger[0];

  const pm25 = aqi?.pm25 ?? null;
  const level: BriefLevel =
    pm25 != null && pm25 >= 75 ? "warning" : pm25 != null && pm25 >= 37.5 ? "watch" : "good";

  const value = pm25 != null
    ? { en: `PM2.5 ${num(pm25, 0, " µg/m³")}${aqi?.station ? ` at ${aqi.station}` : ""}`, th: `PM2.5 ${num(pm25, 0, " มคก./ลบ.ม.")}${aqi?.station ? ` ที่${aqi.station}` : ""}` }
    : danger?.pm25Live != null
      ? { en: `PM2.5 ${num(danger.pm25Live, 0, " µg/m³")} — ${danger.provinceNameEn}`, th: `PM2.5 ${num(danger.pm25Live, 0, " มคก./ลบ.ม.")} — ${danger.provinceNameEn}` }
      : NO_DATA_VALUE;

  const facts = danger?.washoutBand && danger.washoutBand !== "none" && danger.washoutBand !== "unknown"
    ? { en: `Rain washout: ${danger.washoutBand}`, th: `ฝนช่วยล้าง: ${danger.washoutBand}` }
    : undefined;

  return {
    key: "air",
    level,
    title: { en: "AIR", th: "คุณภาพอากาศ" },
    value,
    facts,
    source: "aqicn",
    ageMinutes: i.ages.air,
  };
}

function incidentsTile(i: DeskSignalsInput): BriefSignal {
  if (!i.ok.incidents) {
    return {
      key: "incidents",
      level: "unknown",
      title: { en: "IN THE STREET", th: "เหตุการณ์ในพื้นที่" },
      value: NO_DATA_VALUE,
      facts: NO_DATA_FACTS,
      source: "itic · city",
      ageMinutes: i.ages.incidents,
    };
  }
  const n = i.incidents.length;
  const level: BriefLevel =
    n >= DESK_BANDS.INCIDENTS_WARNING ? "warning" : n >= DESK_BANDS.INCIDENTS_WATCH ? "watch" : "good";

  return {
    key: "incidents",
    level,
    title: { en: "IN THE STREET", th: "เหตุการณ์ในพื้นที่" },
    value: {
      en: `${count(n)} open incident${n === 1 ? "" : "s"} reported`,
      th: `เหตุการณ์ที่ยังเปิดอยู่ ${count(n)} ราย`,
    },
    source: "itic · city",
    ageMinutes: i.ages.incidents,
  };
}

/**
 * Build the five situation compartments.
 *
 * The order here is the fallback order; `buildMayorBrief` re-orders them by
 * severity so the worst situation is always drawn largest.
 */
export function buildDeskSignals(input: DeskSignalsInput): BriefSignal[] {
  return [
    floodTile(input),
    securityTile(input),
    weatherTile(input),
    airTile(input),
    incidentsTile(input),
  ];
}
